// Angular Core
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// Angular Material
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule, MatRow } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// Angular Common & Animations
import { DatePipe, CommonModule } from '@angular/common';
import {
  style,
  state,
  trigger,
  transition,
  animate
} from '@angular/animations';

// RxJS
import { forkJoin, from, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

// CDK & Third Party
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TableVirtualScrollModule, TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';

// App Services & Interfaces
import { FeedstockService } from '../../services/feedstockservice';
import { DialogService } from '../../services/dialogservice';
import { LookUpService } from '../../services/lookupservice';
import { NotificationService } from '../../services/notificationservice';
import { RolePermission, RolePermissionsService } from '../../services/role-permissions.service';
import { FilterState, FilterStateService } from '../../services/filter-state.service';

// App Components
import { AddeditLoadscheduleComponent } from './addedit-loadschedule/addedit-loadschedule.component';
import { SitelockcodesComponent } from './sitelockcodes/sitelockcodes.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AddEditRevenueComponent } from './add-edit-revenue/add-edit-revenue.component';
import { DispatchInfoComponent } from './dispatch-info/dispatch-info.component';
import { AttachDocumentsComponent as DeepackAttachDocumentsComponent } from '../deepack/attach-documents/attach-documents.component';
import { ViewDocumentsComponent } from './view-documents/view-documents.component';

// App Models & Types
import {
  DispatchInfoDialogData,
  FeedStockDispatchDetails,
  FeedstockListRequest,
  FeedStockLoadInfoResponse,
  FeedstockLoadScheduleResponse,
  FSLoadStatus,
  UpdateFeedStockSampleStatusRequest
} from '../../interfaces/feedstocklistmodel';
import {
  Regions,
  RegionsInfo,
  SitesShortForm
} from '../../interfaces/regions';
import { MatSelectItems } from '../../interfaces/lookup';
import { FeedstockStatus, GetActualVolumeTonsForAmeliaSitesResponse } from '../../interfaces/feedstockstatus';
import { ActionStatus, LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { GetMarketGeneratorsResponse } from '../../interfaces/generatorsmodel';
import { RoleService } from '../../services/role.service';
import { dateRangeCompleteValidator, handleDateKeydown, strictDateValidator } from '../shared/commonutils/helper';
import { formatDateOnly } from '../shared/commonutils/helper';
import { MatTableDataSource } from '@angular/material/table';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { SelectSearchComponent } from '../shared/components/select-search/select-search.component';
import { ColumnSelectorComponent } from './column-selector/column-selector.component';
import { MatTooltipModule } from '@angular/material/tooltip';
@Component({
  selector: 'app-loadschedules',
  standalone: true,
  imports: [MatIcon, MatTableModule, MatRow, MatPaginatorModule, MatSortModule, MatFormFieldModule,
    MatInputModule, MatButtonModule, MatSelectModule, MatDatepickerModule, DatePipe, CommonModule,
    MatMenuModule, MatProgressBarModule, MatProgressSpinnerModule, MatCardModule, FormsModule,
    ReactiveFormsModule, ScrollingModule, TableVirtualScrollModule, MatCheckboxModule,
    NgxMatSelectSearchModule, SelectSearchComponent, MatTooltipModule],
  providers: [provideNativeDateAdapter()],
  templateUrl: './loadschedules.component.html',
  styleUrl: './loadschedules.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})

export class LoadschedulesComponent implements OnInit, OnDestroy {

  permissions = inject(RolePermissionsService);
  markets: Regions[] = [];
  filteredMarkets: Regions[] = [];
  generators: GetMarketGeneratorsResponse[] = [];
  filteredGenerators: GetMarketGeneratorsResponse[] = [];
  filteredSites: SitesShortForm[] = [];
  filteredStatus: FeedstockStatus[] = [];
  marketData: RegionsInfo[] = [];
  sites: SitesShortForm[] = [];
  fsStatus: FeedstockStatus[] = [];
  marketId: number = -1;
  selectStatusItems: MatSelectItems[] = [];
  selectSiteItems: MatSelectItems[] = [];
  selectCustomerItems: MatSelectItems[] = [];
  selectMarketItems: MatSelectItems[] = [];
  isNavigating = false;
  fsstatusId: number = 0;
  generatorId: number | null = 0;
  screenid: string | null = "";
  private currentScreenIdSignal = signal<string>("");
  hasPendingStatusChanges = signal<boolean>(false);
  siteInfo: SitesShortForm = { sid: "", id: null, siteName: "", siteType: null };
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null, [strictDateValidator]),
    end: new FormControl<Date | null>(null, [strictDateValidator]),
  }, { validators: dateRangeCompleteValidator });
  tableVisible = true;
  selectedRows: FeedstockLoadScheduleResponse[] = [];
  checkedFsLoadIds: number[] = [];
  originalStatusDescription = "";
  loadSchedulePermissions: Signal<ActionStatus> = signal(new ActionStatus());
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  baseColumns: string[] = [
    'actions', 'workOrderNumber', 'actualDate', 'sampleStatusDescription', 'materialType',  'streamName', 'tankName',
    'unitOfMeasureName', 'projected_Quantity', 'hauler', 'actual_Quantity', 'actual_Volume_Gallons',
    'select','statusDescription',
    'loadSlipNumber', 'customerReferenceNo',  'notesForCustomer', 'schedulingNotes',
    'notesForHauler', 'notesForDisposalSite'
  ];
  orfColumns: string[] = [
    'actions', 'workOrderNumber', 'actualDate', 'sampleStatusDescription',  'generator', 'streamName', 'pickupLocation',
    'unitOfMeasureName', 'projected_Quantity', 'hauler', 'actual_Quantity',  
    'select','statusDescription',
     'customerReferenceNo', 'notesForCustomer', 'schedulingNotes', 'notesForHauler', 'notesForDisposalSite'
  ];
  spsColumns: string[] = [
    'actions', 'workOrderNumber', 'actualDate',  'generator', 'streamName', 'pickupLocation',
    'siteName', 'unitOfMeasureName', 'projected_Quantity', 'hauler', 'actual_Quantity', 
    'select','statusDescription',
    'customerReferenceNo',  'schedulingNotes', 'notesForCustomer', 'notesForHauler', 'notesForDisposalSite'
  ];
  allColumns = [
    { key: 'actions', label: 'Actions', fixed: true },
    { key: 'workOrderNumber', label: 'Work Order #' },
    { key: 'actualDate', label: 'Date Received' },
    { key: 'sampleStatusDescription', label: 'Sample Status' },
    { key: 'materialType', label: 'Stream Group' },
    { key: 'generator', label: 'Customer' },
    { key: 'streamName', label: 'Stream' },
    { key: 'tankName', label: 'Tank' },
    { key: 'unitOfMeasureName', label: 'Units' },
    { key: 'projected_Quantity', label: 'Projected Quantity' },
    { key: 'hauler', label: 'Hauler' },
    { key: 'actual_Quantity', label: 'Actual Quantity' },
    { key: 'actual_Volume_Gallons', label: 'Actual Vol(gal)' },
    { key: 'statusDescription', label: 'Status' },
    { key: 'loadSlipNumber', label: 'Load Slip#' },
    { key: 'customerReferenceNo', label: 'Customer Ref#' },
    { key: 'notesForCustomer', label: 'Customer Notes' },
    { key: 'schedulingNotes', label: 'Scheduling Notes' },
    { key: 'notesForHauler', label: 'Hauler Notes' },
    { key: 'notesForDisposalSite', label: 'Disposal Site Notes' }
  ];
  biomasTons: number = 0;
  csscolor: string = "";
  canShowBiomasTons: boolean = false;
  selectedColumns = signal<string[]>([]);
  displayedColumns = computed<string[]>(() => {
    const screenId = this.currentScreenIdSignal();

    let defaultCols =
      screenId === '4'
        ? this.spsColumns
        : screenId === '2'
          ? this.orfColumns
          : this.baseColumns;
    const cols =
      this.selectedColumns().length > 0
        ? [...this.selectedColumns()]
        : [...defaultCols];

    // Status controls Select
    const hasStatus = cols.includes('statusDescription');

    // Remove existing Select to avoid duplicates
    const selectIndex = cols.indexOf('select');
    if (selectIndex !== -1) {
      cols.splice(selectIndex, 1);
    }

    // Add Select immediately before Status
    if (hasStatus) {
      const statusIndex = cols.indexOf('statusDescription');
      cols.splice(statusIndex, 0, 'select');
    }

    return cols;
    //const saved = this.selectedColumns();

    //return saved.length > 0 ? saved : defaultCols;
  });
  dataSource = new MatTableDataSource<any>();

  //dataSource = new TableVirtualScrollDataSource<any>();
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  private destroy$ = new Subject<void>();
  filterText: string = '';
  constructor(
    private fsService: FeedstockService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private lookUp: LookUpService,
    private cdRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private filterStateService: FilterStateService,
    private rpService: RolePermissionsService,
    private rolesService: RoleService, private injector: Injector
  ) {
    this.filterText = "";
  }

  ngOnInit(): void {
    this.marketId = -1;
    this.filterText = "";
    this.setupFilterPredicate();
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    today.setDate(today.getDate() + 14);
    this.range.controls['start'].setValue(firstDayOfCurrentWeek);
    this.range.controls['end'].setValue(today);
    // this.loadUserGridColumnPreferences();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const newScreenId = params.get('id');
      if (!newScreenId) return;

      const screenInfo = this.getScreenName(`${newScreenId}`);
      // If getScreenActions returns a Signal, call it:
      this.loadSchedulePermissions = this.permissions.getScreenActions(screenInfo.enum);
      

      if (newScreenId) {
        this.resetPendingChanges();
      }
      const hasScreenIdChanged = this.screenid !== newScreenId;
      const prevScreenId = this.screenid;
      this.screenid = newScreenId;
      if (hasScreenIdChanged) {
        this.currentScreenIdSignal.set(newScreenId);
      }
      var currentState = this.filterStateService.getCurrentState(prevScreenId ?? '');
      if (hasScreenIdChanged) {
        this.isNavigating = true;
        this.cdRef.detectChanges();
        if (['1', '2', '4'].includes(this.screenid!) && hasScreenIdChanged) {
          this.initializeDefaultFilters();
        }
        if (['1', '2', '4'].includes(this.screenid!) && currentState != null) {
          this.marketId = currentState.marketId;
          this.siteInfo = { ...currentState.siteInfo };
          this.fsstatusId = currentState.fsstatusId;
          this.generatorId = currentState.generatorId;
          this.filterStateService.updateFilterState(this.screenid!, {
            screenId: this.screenid,
            marketId: this.marketId,
            siteInfo: this.siteInfo,
            fsstatusId: this.fsstatusId,
            generatorId: this.generatorId
          })
        }
        this.getLoadSchedules();
        setTimeout(() => {
          this.tableVisible = true;
          this.cdRef.detectChanges(); // Final check to render the new structure
        }, 0);
      } else {
        this.getLoadSchedules();
        this.cdRef.detectChanges();
      }
    });

    effect(() => {
      // This code will run EVERY TIME the permissions signal changes
      const currentPerms = this.loadSchedulePermissions();
      const canAdd = this.canAddLoad();

    }, { injector: this.injector });

    this.range.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.filterStateService.updateFilterState(this.screenid!, { range: this.range });
    });
  }

  private initializeDefaultFilters(): void {
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    today.setDate(today.getDate() + 14);

    const initialRange = new FormGroup({
      start: new FormControl<Date | null>(firstDayOfCurrentWeek),
      end: new FormControl<Date | null>(today)
    });

    this.marketId = -1;
    this.siteInfo = { sid: "", id: null, siteName: "", siteType: null };
    this.fsstatusId = 0,
      this.range = initialRange;
    this.generatorId = 0;
  }

  trackByFn(index: number, item: any) {
    return item.id;
  }

  getLoadSchedules() {
    let scrnId = Number(this.screenid);
    let screenName = "";
    switch (scrnId) {
      case 1: screenName = ScreenName.ADGrid; break;
      case 2: screenName = ScreenName.ORAGrid; break;
      case 4: screenName = ScreenName.SPSGrid; break;
    }
    let roles = this.rolesService.getRolesAsString();
    forkJoin({
      markets: this.lookUp.getMarketsList(""),
      fsStatus: this.fsService.getFeedStockStatuses(roles),
      sites: this.lookUp.getSitesByMarketId(this.marketId, Number(this.screenid)),
      adsites: this.lookUp.getADSitesByMarketId(this.marketId, Number(this.screenid)),
      gens: this.fsService.getmarketGenerators(this.marketId, false),
      perms: this.rpService.getRolePermissionsNew(roles, screenName, 0),
      dat: this.fsService.getActualVolumeTonsForAmeliaSites()
    }).subscribe({
      next: (response: {
        markets: Regions[], fsStatus: FeedstockStatus[], sites: SitesShortForm[],
        gens: GetMarketGeneratorsResponse[], adsites: SitesShortForm[], perms: LoadScheduleControlFlags,
        dat: GetActualVolumeTonsForAmeliaSitesResponse
      }) => {        
        this.markets = response.markets;
        // this.filteredMarkets = [...this.markets];
        this.fsStatus = response.fsStatus;
        // this.filteredStatus = [...this.fsStatus];
        if (this.screenid == "1") {
          this.sites = response.adsites
        } else {
          this.sites = response.sites;
        }
        // this.filteredSites = [...this.sites];
        this.generators = response.gens;
        // this.filteredGenerators = [...this.generators];
        this.getFeedStockGridData();

        this.selectSiteItems = this.sites.map(item => ({
          id: item.id!,
          name: item.siteName
        }));
        this.selectStatusItems = this.fsStatus.map(item => ({
          id: item.id!,
          name: item.description!
        }));
        this.selectCustomerItems = this.generators.map(item => ({
          id: item.id!,
          name: item.customerName!
        }));
        this.selectMarketItems = this.markets.map(item => ({
          id: item.id!,
          name: item.regionName!
        }));

        this.biomasTons = response.dat.actual_Volume_Tons;
        this.csscolor = response.dat.colorCode;

        console.log("Ctrl permissions", response.perms);
        this.controlPermissions = response.perms;
        this.cdRef.detectChanges();
        this.isNavigating = false;
      }
    });
  }

  getMarketsData() {
    this.lookUp.getMarketsList("").subscribe({
      next: async (response) => {
        this.markets = response;
        this.filteredMarkets = [...this.markets];
        if (this.markets && this.markets.length > 0) {
          this.marketId = -1;
          this.getDisposalSitesByMarketId(this.marketId, 1);
        }
      }
    })
  }

  getDisposalSitesByMarketId(marketId: number, scheduleTypeId: number) {
    this.lookUp.getSitesByMarketId(marketId, scheduleTypeId).subscribe({
      next: async (response) => {
        
        this.sites = response;
        this.filteredSites = [...this.sites];

        this.selectSiteItems = this.sites.map(item => ({
          id: item.id!,
          name: item.siteName
        }));
      }
    })
  }

  getADDisposalSitesByMarketId(marketId: number, scheduleTypeId: number) {
    this.lookUp.getADSitesByMarketId(marketId, scheduleTypeId).subscribe({
      next: async (response) => {
        
        this.sites = response;
        this.filteredSites = [...this.sites];

        this.selectSiteItems = this.sites.map(item => ({
          id: item.id!,
          name: item.siteName
        }));
      }
    })
  }

  getFeedstockStatus() {
    this.fsService.getFeedStockStatus().subscribe({
      next: async (response) => {
        this.fsStatus = response;
        this.filteredStatus = [...this.fsStatus];
      }
    })
  }

  getFeedStockGridData() {
    const siteid = this.siteInfo?.sid || '';
    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate = this.range.value.end ? formatDateOnly(this.range.value.end) : '';

    this.range.markAllAsTouched();
    if (this.range.invalid) {
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.cdRef.detectChanges();
      return;
    }
    const requestBody = this.buildFeedstockRequest(fromDate ?? '', toDate ?? '');
    this.fsService.getFeedstockList(requestBody).subscribe(
      {
        next: (res: FeedstockLoadScheduleResponse[]) => {
          console.log("load data", res);
          this.checkedFsLoadIds = [];
          this.tableVisible = true;

          this.dataSource.data = res;
          this.dataSource.sort = this.sort;
          if (res && res.length > 0) {
          }
        }
      }
    );
  }

  resetPendingChanges(): void {
    this.selectedRows = [];
    this.hasPendingStatusChanges.set(false);
    this.originalStatusDescription = '';
  }

  onCustomerReceived(event: number | null) {
    this.generatorId = event ?? 0;
  }

  onMarketReceived(event: number | null) {
    this.marketId = event ?? -1;
    if(this.isNavigating) return;
    if(this.screenid == '4'){
      this.fsService.getmarketGenerators(this.marketId, false).subscribe({
        next: (res: any[]) => {
          this.generators = res;
          this.selectCustomerItems = this.generators.map(item => ({
            id: item.id!,
            name: item.customerName!
          }));
        }
      }
      );
    } else {
      this.getADDisposalSitesByMarketId(this.marketId, Number(this.screenid));
    }
  }

  onSiteReceived(event: number | null) {
    if(this.isNavigating) return;
    this.siteInfo.id = event ?? 0;
    this.siteInfo.sid = event == null ? '' : 'VRS-' + event?.toString();

    let selectedSite = this.selectSiteItems.find(i => i.id == event);
    if(selectedSite?.name.toLowerCase().startsWith("amelia")){
      this.canShowBiomasTons = true;
    }else{
      this.canShowBiomasTons = false;
    }
  }

  onStatusReceived(event: number | null) {
    this.fsstatusId = event ?? 0;
  }

  private buildFeedstockRequest(fromDate: string, toDate: string): FeedstockListRequest {
    return {
      marketIds:      this.marketId.toString(),
      genIds:         this.generatorId === 0 ? '' : `${this.generatorId}`,
      streamIds:      '',
      siteIds:        this.siteInfo?.sid || '-1',
      fromDate:       fromDate,
      toDate:         toDate,
      scheduleTypeId: Number(this.screenid),
      statusId:       this.fsstatusId
    };
  }

  onSearch() {
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    this.cdRef.detectChanges();

    if (this.range.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.cdRef.detectChanges();
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    if (this.hasPendingStatusChanges()) {
      //this.notificationService.show("Please update the pending status changes before performing a new search.", 'warning');
      this.pendingDialog();
      return;
    }

    this.executeSearch();
  }

  pendingDialog() {
    this.dialog.open(ConfirmationDialogComponent, {
      disableClose: false,
      panelClass: 'delete-confirmation-dialog-nowrap',
      data: {
        title: 'Alert',
        message: 'You have unsaved status changes.<br /> Would you like to save them?',
        confirmButtonText: 'Update Status',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'primary'
      } as ConfirmationDialogData
    }).afterClosed().subscribe((result: boolean | undefined) => {
      if (result === undefined || result === null) return; // X clicked — do nothing
      if (result === true) {
        this.updateStatusThenSearch();  // Save then search
      } else {
        this.resetPendingChanges();     // Discard then search
        this.executeSearch();
      }
    });
  }

  updateStatusThenSearch(): void {
    const payload: FSLoadStatus[] = this.selectedRows.map(row => ({
      loadScheduleId: row.id,
      status: row.status
    }));
    this.fsService.UpdateFeedstockLoadStatus(payload).subscribe({
      next: () => {
        this.hasPendingStatusChanges.set(false);
        this.selectedRows = [];
        this.executeSearch();
      }
    });
  }

  updateStatusAndNavigate(): Observable<boolean> {
    this.hasPendingStatusChanges.set(false);
    const payload: FSLoadStatus[] = this.selectedRows.map(row => ({
      loadScheduleId: row.id,
      status: row.status
    }));
    return this.fsService.UpdateFeedstockLoadStatus(payload).pipe(
      map(() => {
        this.selectedRows = [];
        return true;  // Allow navigation after save completes
      })
    );
  }

  private executeSearch(): void {
    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate   = this.range.value.end   ? formatDateOnly(this.range.value.end)   : '';
    
    this.filterStateService.updateFilterState(this.screenid!, {
      screenId: this.screenid,
      marketId: this.marketId,
      siteInfo: this.siteInfo,
      fsstatusId: this.fsstatusId,
      generatorId: this.generatorId
    })
    const requestBody = this.buildFeedstockRequest(fromDate ?? '', toDate ?? '');

    this.fsService.getFeedstockList(requestBody).subscribe({
      next: (res: FeedstockLoadScheduleResponse[]) => {
        this.checkedFsLoadIds = [];
        this.dataSource.data = res;
        this.dataSource.sort = this.sort;
        this.syncFilter();
      }
    });
  }

  loadMenuDataAndOpen(row: FeedstockLoadScheduleResponse, trigger: MatMenuTrigger): void {
    let scrnId = Number(this.screenid);
    let screenName = "";
    switch (scrnId) {
      case 1: screenName = ScreenName.ADGrid; break;
      case 2: screenName = ScreenName.ORAGrid; break;
      case 4: screenName = ScreenName.SPSGrid; break;
    }
    let roles = this.rolesService.getRolesAsString();
    forkJoin({
      perms: this.rpService.getRolePermissionsNew(roles, screenName, row.id),
      rowData: this.fsService.getFeedStockLoadInfoById(row.id)
    }).subscribe({
      next: (response: {
        rowData: FeedStockLoadInfoResponse, perms: LoadScheduleControlFlags
      }) => {
        this.controlPermissions = response.perms;
        row.menuData = response.rowData;
        row.menuData.sampleStatus = row.sampleStatus;
        trigger.openMenu();
      }
    })
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filterText = filterValue; // Store the current text
    this.syncFilter();
  }

  formatDateForFilter(isoStr: string): string {
    if (!isoStr) return '';
    const [datePart, timePart = '00:00:00'] = isoStr.split('T');
    const [yyyy, mm, dd] = datePart.split('-');
    const [hh, min] = timePart.split(':');
    return `${mm}/${dd}/${yyyy} ${hh}:${min}`;   // → "06/10/2026 00:00"
  }

  private setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (
      data: FeedstockLoadScheduleResponse,
      filter: string
    ): boolean => {
      const formattedDate = this.formatDateForFilter(data.actualDate ?? '');

      const searchable = [
        data.workOrderNumber,
        formattedDate,
        data.statusDescription,
        data.sampleStatusDescription,
        data.materialType,
        data.generator,
        data.streamName,
        data.tankName,
        data.siteName,
        data.pickupLocation,
        data.hauler,
        data.unitOfMeasureName,
        data.projected_Quantity,
        data.actual_Quantity,
        data.actual_Volume_Gallons,
        data.loadSlipNumber,
        data.customerReferenceNo,
        data.notesForCustomer,
        data.schedulingNotes,
        data.notesForHauler,
        data.notesForDisposalSite
      ]
        .map(v => (v ?? '').toString().toLowerCase())
        .join(' ');

      return searchable.includes(filter);
    };
  }

  syncFilter() {
    if (this.dataSource) {
      this.dataSource.filter = this.filterText.trim().toLowerCase();
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  filterMarkets(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredMarkets = [...this.markets];
    } else {
      this.filteredMarkets = this.markets.filter(
        market => market.regionName.toLowerCase().includes(searchTerm)
      );
    }
  }

  filterSites(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredSites = [...this.sites];
    } else {
      this.filteredSites = this.sites.filter(
        m => m.siteName.toLowerCase().includes(searchTerm)
      );
    }
  }

  filterGenerators(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredGenerators = [...this.generators];
    } else {
      this.filteredGenerators = this.generators.filter(
        m => m.customerName.toLowerCase().includes(searchTerm)
      );
    }
  }

  filterStatus(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredStatus = [...this.fsStatus];
    } else {
      this.filteredStatus = this.fsStatus.filter(
        m => m.description?.toLowerCase().includes(searchTerm)
      );
    }
  }

  addRevenue(data: any, workOrderNumber: string, status: string) {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const dialogRef = this.dialog.open(AddEditRevenueComponent, {
      panelClass: 'big-dialog',
      width: '1050px',
      maxWidth: '1050px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        task: 'Add',
        fsLoadId: data,
        workOrderNumber: workOrderNumber,
        fsStatus: status,
        screenId: this.screenid
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
    });
  }

  openDispatchInfo(row: FeedstockLoadScheduleResponse): void {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const dialogData: DispatchInfoDialogData = {
      fsLoadScheduleId: row.id,
      isAd: this.screenid !== "1",
      workOrderNumber: row.workOrderNumber
    };

    const dialogRef = this.dialog.open(DispatchInfoComponent, {
      panelClass: 'big-dialog',
      disableClose: true,
      width: '1050px',
      maxWidth: '1050px',
      maxHeight: '90vh',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.fsService.getFeedStockLoadInfoById(row.id).subscribe({
        next: (data: FeedStockLoadInfoResponse) => {
          const idx = this.dataSource.data.findIndex(r => r.id === row.id);
          if (idx === -1) return;

          const updated = [...this.dataSource.data];
          updated[idx] = {
            ...updated[idx],
            dispatch: data.dispatch,
            menuData: {
              ...data,
              sampleStatus: updated[idx].sampleStatus
            } as any
          };
          this.dataSource.data = updated;
          this.cdRef.detectChanges();
        }
      });
    });
  }

  onOpenLoadDialog(fsId: any, taskType: any, rowData: FeedstockLoadScheduleResponse | null): void {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    let roleNames = this.rolesService.getRolesAsString();
    const showStatusButton = ((roleNames.includes('GFA') || roleNames.includes('GSU')) && rowData?.status === 12);
    let showSaveButton = this.loadSchedulePermissions().ShowPopupSaveButton;
    if (roleNames.includes('GFA') && rowData?.status === 12) {
      showSaveButton = true;
    }
    
    const dialogRef = this.dialog.open(AddeditLoadscheduleComponent, {
      width: '1050px',
      maxWidth: '1050px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        task: taskType,
        fsLoadId: fsId,
        scheduleTypeId: this.screenid,
        screenName: this.getScreenName(this.screenid ?? '').name,
        showSaveButton: showSaveButton,
        showNotesPopuplink: false //this.loadSchedulePermissions().ShowPopupForInternalNotes && rowData?.status === 12) ? true : false,
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === true) {
        this.onSearch();
      } else if (result?.hasAttachment && result?.loadId != null) {
        const loadId = result.loadId as number;
        const idx = this.dataSource.data.findIndex(r => r.id === loadId);
        if (idx !== -1) {
          const updated = [...this.dataSource.data];
          updated[idx] = { ...updated[idx], loadSlip: true };
          this.dataSource.data = updated;   // triggers paperclip visibility
        }
      }
    });
  }

  onDeleteLoad(fsid: number, workOrderNumber: string) {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete Load ${workOrderNumber}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      panelClass: 'delete-confirmation-dialog-nowrap',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        if (fsid !== null && fsid !== undefined) {
          this.fsService.deleteFeedstockloadscheduleById(fsid).subscribe({
            next: () => {
              //this.dataSource.data = this.dataSource.data.filter(item => item.id !== fsid);
              this.onSearch();
            }
          });
        }
      }
    });
  }

  onMarketSelectionChange(selectedId: number): void {
    this.filterStateService.updateFilterState(this.screenid!, { screenId: this.screenid, marketId: selectedId });
    this.onSearch();
  }

  onSiteSelectionChange(): void {
    this.filterStateService.updateFilterState(this.screenid!, { screenId: this.screenid, marketId: this.marketId, siteInfo: this.siteInfo });
    this.onSearch();
  }

  onGeneratorSelectionChange(): void {
    this.filterStateService.updateFilterState(this.screenid!, { screenId: this.screenid, marketId: this.marketId, generatorId: this.generatorId });
    this.onSearch();
  }

  onStatusChange(): void {
    this.filterStateService.updateFilterState(this.screenid!, { screenId: this.screenid, marketId: this.marketId, siteInfo: this.siteInfo, fsstatusId: this.fsstatusId });
    this.onSearch();
  }

  getFirstDayOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  }

  openLockCodesBySiteId() {
    if (this.hasPendingStatusChanges()) {
      this.notificationService.show("Please update the pending status changes before performing a new search.", 'warning');
      return;
    }
    if (this.siteInfo === null || this.siteInfo.sid === "") {
      this.notificationService.show("Please select a Disposal site first.", 'warning');
      return;
    }
    this.dialogService.openDialog(SitelockcodesComponent, {
      panelClass: 'siteLockdialog',
      disableClose: true,
      data: {
        siteId: this.siteInfo.sid,
        siteName: this.siteInfo.siteName
      }
    });
  }

  openAttachDocuments(id: number, workOrderNumber: string): void {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const key = `documents_${id}`;
    localStorage.removeItem(key);

    const dialogRef = this.dialog.open(DeepackAttachDocumentsComponent, {
      panelClass: 'attachDocumentsDialog',
      disableClose: true,
      data: {
        loadScheduleId: id,
        screenTypeId: '0',
        name: workOrderNumber,
        disableAddMoreFiles: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onSearch();
      }
    });
  }

  openViewDocsDialog(loadScheduleId: number, workOrderNumber: string, row: FeedstockLoadScheduleResponse): void {
    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const dialogRef = this.dialog.open(ViewDocumentsComponent, {
      //panelClass: 'viewDocsDialog',
      disableClose: true,
      data: {
        loadScheduleId: loadScheduleId,
        screenTypeId: '0',
        name: workOrderNumber,
        canShowGridActions: this.controlPermissions.showDocumentDeleteButton
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        row.loadSlip = false;

        this.dataSource.data = this.dataSource.data.map(x =>
          x.id === row.id ? { ...x, loadSlip: false } : x
        );
      }
    });
  }

  compareSites(site1: SitesShortForm | null, site2: SitesShortForm | null): boolean {
    if (!site1 || !site2) {
      return site1 === site2;
    }
    return site1.sid === site2.sid;
  }

  getSampleStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      S: 'Take Sample',
      T: 'Sample Taken',
      L: 'Sample Sent To Lab'
    };
    if (!status || status === 'null') return '';
    return statusMap[status] ?? '';
  }

  setSampleStatus(row: FeedstockLoadScheduleResponse, newStatus: string, testingType: string| null): void {

    if (this.hasPendingStatusChanges()) {
      this.pendingDialog();
      return;
    }
    const index = this.dataSource.data.findIndex(item => item.id === row.id);
    if (index > -1) {
      const updatedData = [...this.dataSource.data];
      updatedData[index] = { ...updatedData[index], sampleStatus: newStatus };
      this.dataSource.data = updatedData;
    }
    var data: UpdateFeedStockSampleStatusRequest = {
      feedStockLoadScheduleId: row.id,
      sampleStatus: newStatus,
      testingTypes: testingType
    };
    this.fsService.UpdateFeedstockSampleStatus(data).subscribe({
      next: (res) => {
        this.notificationService.show('Sample status updated successfully!', 'success');
        this.getFeedStockGridData();
      }
    });
  }

  getScreenName(scheduleTypeId: string): { name: string, enum: ScreenName | '' } {
    switch (scheduleTypeId) {
      case "1":
        return { name: "AD", enum: ScreenName.ADSchedule };
      case "2":
        return { name: "ORF", enum: ScreenName.ORF };
      case "4":
        return { name: "Strategic", enum: ScreenName.SPS };
      default:
        return { name: "", enum: '' };
    }
  }

  onCheckboxChange(row: FeedstockLoadScheduleResponse, event: any): void {
    if (event.checked) {
      this.checkedFsLoadIds.push(row.id);
    } else {
      const index = this.checkedFsLoadIds.indexOf(row.id);
      if (index > -1) {
        this.checkedFsLoadIds.splice(index, 1);
      }
    }
  }

  canAddLoad = computed(() => {
    const perms = this.controlPermissions;
    return perms.addButton;
  });

  isUpdateDisabled = computed(() => {
    const perms = this.controlPermissions.updateStatus;
    const hasChanges = this.hasPendingStatusChanges(); // Your 'dirty' state signal
    const hasSelection = this.selectedRows.length > 0;

    // Logic: Disable the button if:
    // - The user doesn't have security permission
    // - OR no rows are selected
    // - OR there are no pending changes to save
    return !perms || !hasSelection || !hasChanges;
  });
  

  canShowSampleNextAction = computed(() => {
    const perms = this.loadSchedulePermissions();
    const screenid = this.currentScreenIdSignal();
    switch (screenid) {
      case '1': return perms.ShowADGridSampleStatus;
      default: return false;
    }
  });

  dispatchLoads(): void {
    if (this.hasPendingStatusChanges()) {
      this.notificationService.show("Please update the pending status changes before performing a new search.", 'warning');
      return;
    }
    var dispatchLoadsPayload: FeedStockDispatchDetails[] = this.checkedFsLoadIds.map(id => ({ fsLoadScheduleId: id }));

    if (dispatchLoadsPayload.length === 0) {
      return;
    }
    this.fsService.dispatchFeedStockLoads(dispatchLoadsPayload, this.screenid ?? '1')
      .subscribe({
        next: () => {
          this.checkedFsLoadIds = [];
          this.onSearch();
        }
      });
  }

  updateStatus(): void {
    this.hasPendingStatusChanges.set(false);
    var payload: FSLoadStatus[] = this.selectedRows.map(row => ({
      loadScheduleId: row.id,
      status: row.status
    }));
    this.fsService.UpdateFeedstockLoadStatus(payload).subscribe({
      next: (response: boolean) => {
        this.onSearch();
        this.selectedRows = [];
      }
    });
  }

  onStatusClick(row: FeedstockLoadScheduleResponse): void {
    if (row.statusDescription === 'Invoiced') {
      return;
    }
    row.isEditingStatus = true;
    this.dataSource.data.forEach(row => {
      row.isEditingStatus = false;
    });

    row.isEditingStatus = true;
    this.originalStatusDescription = row.statusDescription;
    if (row.originalStatusDescription === undefined) {
      row.originalStatusDescription = row.statusDescription;
    }
  }

  isRowDisabled(row: FeedstockLoadScheduleResponse): boolean {
    const isAdsRecord = !!row.adsFlag;

    // Disable when generator 128 has a negative projected quantity
    const isGen128NegativeVolume = Number(row.generatorId) === 128
      && (row.projected_Quantity ?? 0) < 0;

    return isAdsRecord || isGen128NegativeVolume;
  }

  getRowClass(row: FeedstockLoadScheduleResponse): { [key: string]: boolean } {
    const isAdsRecord = !!row.adsFlag;

    const isGen128NegativeVolume = Number(row.generatorId) === 128 && (row.projected_Quantity ?? 0) < 0;
    const statusToColor = row.originalStatusDescription || row.statusDescription;
    const dispatchCount = row.dispatch ?? row.menuData?.dispatch ?? 0;
    const isDispatched = statusToColor === 'Projected' && dispatchCount;
    const dayClass: { [key: string]: boolean } = {};
    if (row.scheduleDayOfWeek) {
      dayClass[row.scheduleDayOfWeek] = true;
    }

    return {
      ...dayClass,
      lcan: statusToColor === 'Customer Cancelled',
      orangeBG: statusToColor === 'Projected',
      tomatoredBG: statusToColor === 'VR Cancelled' || statusToColor === 'Rejected',
      yellowBG: statusToColor === 'Customer Cancelled',
      disabledColor: isAdsRecord || isGen128NegativeVolume
      //dispatchColor: isDispatched
    };
  }

  getActionsCellClass(row: FeedstockLoadScheduleResponse): { [key: string]: boolean } {
    const statusToColor = row.originalStatusDescription || row.statusDescription;

    const dispatchCount = row.dispatch ?? row.menuData?.dispatch ?? 0;
    const isDispatched = !dispatchCount;
    return {
      nodispatchIndicator: isDispatched
    }
  }

  disableCheckBox(row: FeedstockLoadScheduleResponse) {
    const dispatchCount = row.dispatch ?? row.menuData?.dispatch ?? 0;
    const isDispatched = !dispatchCount;
    return isDispatched;
  }

  getSampleStatusCellColor(row: FeedstockLoadScheduleResponse){
    if(row.sampleStatusDescription.toLowerCase().includes("permit")){
      return "sampleStatus";
    }
    return "";
  }

  getStatusCellClass(row: FeedstockLoadScheduleResponse): { [key: string]: boolean } {
    const statusIdMap: { [key: number]: string } = {
      9: 'orangeBG',    // Projected
      2: 'tomatoredBG', // VR Cancelled
      5: 'tomatoredBG', // Rejected
      3: 'lcan',       // Customer Cancelled
      7: 'dispatchColor' //Dispatched
    };

    const isPending = row.originalStatusDescription !== undefined && row.originalStatusDescription !== row.statusDescription;

    let pendingclassName = statusIdMap[row.status];
    pendingclassName = '';
    return {
      ...(pendingclassName ? { [pendingclassName]: true } : {}),
      'pending-status-change': isPending
    };
  }

  onStatusDropdownClose(element: FeedstockLoadScheduleResponse): void {
    const newStatus = this.fsStatus.find(s => s.id === element.status);
    if (newStatus && newStatus.description !== this.originalStatusDescription) {
      element.statusDescription = newStatus.description ?? '';

      const existingIndex = this.selectedRows.findIndex(row => row.id === element.id);
      if (existingIndex > -1) {
        this.selectedRows[existingIndex] = { ...element };
      } else {
        this.selectedRows.push({ ...element });
      }
    } else {
      const originalStatus = this.fsStatus.find(s => s.description === this.originalStatusDescription);
      if (originalStatus) {
        element.status = originalStatus.id;
      }
      const index = this.selectedRows.findIndex(row => row.id === element.id);
      if (index > -1) {
        this.selectedRows.splice(index, 1);
      }
    }
    element.isEditingStatus = false;
    this.hasPendingStatusChanges.set(this.selectedRows.length > 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getIconBgClass(row: any): any {
    return { iconBGColor: row.menuData?.revenueRecord > 0 };
  }

  canShowDelete(row: any): boolean {
    return row.menuData?.statusDescription !== 'Invoiced';
  }

  getDispatchColorClass(row: any): any {
    return { dispatchColor: row.menuData?.dispatch > 0 };
  }

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
  openColumnDialog(): void {

    const dialogRef = this.dialog.open(ColumnSelectorComponent, {
      width: '450px',
      disableClose: true,
      data: {
        allColumns: this.allColumns,
        selectedColumns: this.displayedColumns()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.selectedColumns.set(result);

      localStorage.setItem(
        `loadschedule-columns-${this.screenid}`,
        JSON.stringify(result)
      );
    });
  }
  private loadUserGridColumnPreferences() {

    this.fsService.getUserGridColumnPreferences(1).subscribe(res => {
      const cols = JSON.parse(res.columnsJson);
      this.selectedColumns.set(cols);
    });
  }
  //openColumnDialog(): void {

  //  this.fsService.getGridColumns(Number(this.screenid)).subscribe({
  //    next: (columns) => {

  //      const dialogRef = this.dialog.open(ColumnSelectorComponent, {
  //        width: '450px',
  //        disableClose: true,
  //        data: {
  //          allColumns: columns,
  //          selectedColumns: columns
  //            .filter(x => x.selected)
  //            .map(x => x.key)
  //        }
  //      });

  //      dialogRef.afterClosed().subscribe(result => {
  //        if (!result) {
  //          return;
  //        }

  //        this.selectedColumns.set(result);

  //        this.fsService.saveGridColumns({
  //          screenId: Number(this.screenid),
  //          selectedColumns: result
  //        }).subscribe();
  //      });
  //    }
  //  });
  //}
}
